import { useEffect, useState } from "react";
import { ChronoUnit, DateTimeFormatter, Duration, LocalDate, LocalDateTime } from "@js-joda/core";
import { Locale } from "@js-joda/locale_en-us";
import { Card } from "react-bootstrap";
import { CalendarEvent, Alarm } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";

import { showDurationShort, showTimeShort } from "../Data/Date";
import { showMoney          } from "../Data/Money";
import { Reservation        } from "../Data/ApiTypes";
import { facilityImg        } from "../Data/Common";

export type ReservationStatus = 'active' | 'upcoming' | 'expired';

export function getReservationStatus(r: Reservation): ReservationStatus {
    const now = LocalDateTime.now();
    if (r.end.isBefore(now))   return 'expired';
    if (r.start.isAfter(now))  return 'upcoming';
    return 'active';
}

const shortMonthDay = DateTimeFormatter.ofPattern("MMM d").withLocale(Locale.US);

function showReservationDateRange(start: LocalDateTime, end: LocalDateTime): string {
    const startDate = start.toLocalDate();
    const dateLabel = startDate.equals(LocalDate.now()) ? "Today" : startDate.format(shortMonthDay);
    return `${dateLabel}  ·  ${showTimeShort(start.toLocalTime())} - ${showTimeShort(end.toLocalTime())}`;
}

const STATUS_CONFIG: Record<ReservationStatus, { label: string; dotClass: string }> = {
    active:   { label: 'In Progress', dotClass: 'bg-success'   },
    upcoming: { label: 'Upcoming',    dotClass: 'bg-primary'   },
    expired:  { label: 'Expired',     dotClass: 'bg-secondary' },
};

function useMinuteTick() {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 60_000);
        return () => clearInterval(id);
    }, []);
}

function ActiveFooter({ start, end }: { start: LocalDateTime; end: LocalDateTime }) {
    useMinuteTick();

    const now = LocalDateTime.now();
    const totalMinutes   = start.until(end, ChronoUnit.MINUTES);
    const elapsedMinutes = start.until(now, ChronoUnit.MINUTES);
    const remainingMin   = Math.max(0, now.until(end, ChronoUnit.MINUTES));
    const pct = totalMinutes > 0
        ? Math.min(100, Math.max(0, (elapsedMinutes / totalMinutes) * 100))
        : 0;

    return <>
        <div className="d-flex justify-content-between small mb-1">
            <span className="text-muted">{showTimeShort(start.toLocalTime())}</span>
            <span className="fw-semibold text-dark">
                {showDurationShort(Duration.ofMinutes(remainingMin))} left
            </span>
            <span className="text-muted">{showTimeShort(end.toLocalTime())}</span>
        </div>
        <div className="progress" style={{ height: 6, borderRadius: 3 }}>
            <div className="progress-bar bg-dark" style={{ width: `${pct}%`, borderRadius: 3 }} />
        </div>
    </>;
}

function UpcomingFooter({ start }: { start: LocalDateTime }) {
    const daysUntil = LocalDate.now().until(start.toLocalDate(), ChronoUnit.DAYS);
    const label =
        daysUntil === 0  ? "Starts today" :
        daysUntil === 1  ? "Starts tomorrow" :
                           `Starts in ${daysUntil} days`;

    return (
        <span className="d-flex align-items-center gap-1 fw-semibold" style={{ color: '#e8590c' }}>
            <Alarm size={14} />
            {label}
        </span>
    );
}

function ExpiredFooter({ end }: { end: LocalDateTime }) {
    return (
        <span className="text-muted small">
            Ended {end.toLocalDate().format(shortMonthDay)} at {showTimeShort(end.toLocalTime())}
        </span>
    );
}

export function ReservationCard({ reservation: r }: { reservation: Reservation }) {
    const nav = useNavigate();
    const status = getReservationStatus(r);
    const { label, dotClass } = STATUS_CONFIG[status];
    const f = r.facility;

    return (
        <Card className="mb-3" style={{ cursor: "pointer" }}
              onClick={() => nav(`/user/reservation/details/${r.reservationId}`)}>
            <Card.Header className="text-start d-flex align-items-center justify-content-between gap-2 py-1">
                <span className="d-flex align-items-center gap-1">
                    <span className="d-inline-flex align-items-center gap-2 rounded-pill px-2 py-1 small"
                          style={{ color: 'var(--bs-body-color)' }}>
                        <span className={`rounded-circle ${dotClass}`} style={{ width: 8, height: 8 }} aria-hidden />
                        {label}
                    </span>
                </span>
                <span className="text-muted small text-end d-flex align-items-center gap-1">
                    <CalendarEvent size={12} />
                    {showReservationDateRange(r.start, r.end)}
                </span>
            </Card.Header>
            <Card.Body className="purchase-layout gap-2">
                <div className="grid-area-a">
                    <img className="facility-result-pic"
                        src={facilityImg(f.listImageId)}
                        style={{ aspectRatio: "1 / 1", objectFit: "cover" }} />
                </div>
                <div className="grid-area-b d-flex flex-column justify-content-center my-2">
                    <div className="fw-bold">{f.name}</div>
                    <div className="text-muted small">
                        {f.address.addressLine1}, {f.address.city}
                    </div>
                </div>
                <div className="grid-area-c d-flex flex-column align-items-end justify-content-center">
                    <div className="fw-bold fs-5">{showMoney(r.price)}</div>
                    <div className="text-muted small">for {showDurationShort(r.duration)}</div>
                </div>
            </Card.Body>
            <Card.Footer className="bg-white py-2">
                {status === 'active'   && <ActiveFooter   start={r.start} end={r.end} />}
                {status === 'upcoming' && <UpcomingFooter  start={r.start} />}
                {status === 'expired'  && <ExpiredFooter   end={r.end} />}
            </Card.Footer>
        </Card>
    );
}
